/**
 * APA 7th edition bibliography formatter.
 *
 * Rules implemented:
 * - Author format: LastName, F. M.
 * - 1 author: Author (year). Title. Journal, vol(issue), pages. https://doi.org/…
 * - 2–20 authors: all, ampersand before last
 * - 21+ authors: first 19, …, last author
 * - Sorted alphabetically by first author's last name (caller responsibility)
 * - Italic journal name + volume (simulated in plain text with no markup)
 *
 * Supported types: article-journal, book, chapter, thesis, report, webpage
 */

import type {
    CslJson,
    ReferenceItem,
} from '@/components/references/ReferenceListItem.vue';

// ── Author helpers ────────────────────────────────────────────────────────────

interface CslName {
    family?: string;
    given?: string;
    literal?: string;
}

function initials(given: string): string {
    return given
        .trim()
        .split(/\s+/)
        .map((p) => (p[0] ? p[0].toUpperCase() + '.' : ''))
        .filter(Boolean)
        .join(' ');
}

function formatCslAuthor(name: CslName): string {
    if (name.literal) {
        return name.literal;
    }

    const family = name.family ?? '';
    const given = name.given ? initials(name.given) : '';

    return given ? `${family}, ${given}` : family;
}

function formatCslAuthorList(authors: CslName[]): string {
    if (authors.length === 0) {
        return '';
    }

    const formatted = authors.map(formatCslAuthor);

    if (formatted.length === 1) {
        return formatted[0];
    }

    if (formatted.length <= 20) {
        const last = formatted[formatted.length - 1];

        return formatted.slice(0, -1).join(', ') + ', & ' + last;
    }

    // 21+ → first 19, ..., last
    return (
        formatted.slice(0, 19).join(', ') +
        ', ..., ' +
        formatted[formatted.length - 1]
    );
}

// ── Plain-text author from string array (fallback when no CSL) ───────────────

function formatPlainAuthor(fullName: string): string {
    const parts = fullName.trim().split(/\s+/);

    if (parts.length < 2) {
        return fullName;
    }

    // Detect "LastName Initial(s)" format — e.g. "Martinez A" or "Smith J M"
    // If all non-first parts are single letters (with optional period), treat as already
    // in LastName-first format and convert to "LastName, I." style.
    const tail = parts.slice(1);
    const isLastNameFirst = tail.every((p) => /^[A-Za-z]\.?$/.test(p));

    if (isLastNameFirst) {
        const lastName = parts[0];
        const initials = tail.map((p) => p.replace('.', '') + '.').join(' ');

        return `${lastName}, ${initials}`;
    }

    // Standard "FirstName ... LastName" format
    const last = parts[parts.length - 1];
    const rest = parts
        .slice(0, -1)
        .map((p) => (p[0] ?? '') + '.')
        .join(' ');

    return `${last}, ${rest}`;
}

function formatPlainAuthorList(authors: string[]): string {
    const nonEmpty = authors.filter((a) => a.trim().length > 0);

    if (nonEmpty.length === 0) {
        return '';
    }

    const formatted = nonEmpty.map(formatPlainAuthor);

    if (formatted.length === 1) {
        return formatted[0];
    }

    if (formatted.length <= 20) {
        const last = formatted[formatted.length - 1];

        return formatted.slice(0, -1).join(', ') + ', & ' + last;
    }

    return (
        formatted.slice(0, 19).join(', ') +
        ', ..., ' +
        formatted[formatted.length - 1]
    );
}

// ── Year extraction ───────────────────────────────────────────────────────────

function cslYear(csl: CslJson): string {
    const y = csl.issued?.['date-parts']?.[0]?.[0];

    return y && Number(y) > 0 ? String(y) : '';
}

/** Assemble "Author (year). rest" — uses n.d. when year is absent. */
function buildEntry(authorStr: string, year: string, rest: string): string {
    const y = year || 'n.d.';
    const head = authorStr ? `${authorStr} (${y}).` : `(${y}).`;

    return `${head} ${rest}`;
}

// ── Per-type formatters ───────────────────────────────────────────────────────

function formatArticleJournal(csl: CslJson): string {
    const authorStr = formatCslAuthorList(csl.author ?? []);
    const year = cslYear(csl);
    const title = csl.title ?? '';
    const journal = csl['container-title'] ?? '';
    const vol = csl.volume ?? '';
    const issue = csl.issue ? `(${csl.issue})` : '';
    const pages = csl.page ?? '';
    const doi = csl.DOI ? ` https://doi.org/${csl.DOI}` : '';

    let source = journal;

    if (vol) {
        source += `, ${vol}${issue}`;
    }

    if (pages) {
        source += `, ${pages}`;
    }

    return buildEntry(
        authorStr,
        year,
        `${title}.${source ? ' ' + source + '.' : ''}${doi}`,
    );
}

function formatBook(csl: CslJson): string {
    const authorStr = formatCslAuthorList(csl.author ?? []);
    const year = cslYear(csl);
    const title = csl.title ?? '';
    const edition = csl.edition ? ` (${csl.edition} ed.)` : '';
    const publisher = csl.publisher ?? '';
    const doi = csl.DOI ? ` https://doi.org/${csl.DOI}` : '';
    const url = !csl.DOI && csl.URL ? ` ${csl.URL}` : '';

    return buildEntry(
        authorStr,
        year,
        `${title}${edition}. ${publisher}.${doi}${url}`,
    );
}

function formatChapter(csl: CslJson): string {
    const authorStr = formatCslAuthorList(csl.author ?? []);
    const year = cslYear(csl);
    const title = csl.title ?? '';
    const bookTitle = csl['container-title'] ?? '';
    const editors = (csl.editor ?? []).map(formatCslAuthor).join(', ');
    const editorStr = editors ? ` In ${editors} (Eds.),` : '';
    const pages = csl.page ? ` (pp. ${csl.page})` : '';
    const publisher = csl.publisher ?? '';
    const doi = csl.DOI ? ` https://doi.org/${csl.DOI}` : '';

    return buildEntry(
        authorStr,
        year,
        `${title}.${editorStr} ${bookTitle}${pages}. ${publisher}.${doi}`,
    );
}

function formatThesis(csl: CslJson): string {
    const authorStr = formatCslAuthorList(csl.author ?? []);
    const year = cslYear(csl);
    const title = csl.title ?? '';
    const genre = csl.genre ?? 'Thèse';
    const publisher = csl.publisher ?? '';
    const doi = csl.DOI ? ` https://doi.org/${csl.DOI}` : '';
    const url = !csl.DOI && csl.URL ? ` ${csl.URL}` : '';

    return buildEntry(
        authorStr,
        year,
        `${title} [${genre}]. ${publisher}.${doi}${url}`,
    );
}

function formatWebpage(csl: CslJson): string {
    const authorStr = formatCslAuthorList(csl.author ?? []);
    const year = cslYear(csl);
    const title = csl.title ?? '';
    const siteName = csl['container-title'] ?? '';
    const url = csl.URL ? ` ${csl.URL}` : '';

    const siteStr = siteName ? ` ${siteName}.` : '';

    return buildEntry(authorStr, year, `${title}.${siteStr}${url}`);
}

// ── Fallback using plain string authors ───────────────────────────────────────

function formatFallback(ref: ReferenceItem): string {
    const authors = Array.isArray(ref.authors)
        ? ref.authors
        : [ref.authors ?? ''];
    const authorStr = formatPlainAuthorList(authors);
    const year = ref.year && ref.year > 0 ? String(ref.year) : '';
    const doi = ref.doi ? ` https://doi.org/${ref.doi}` : '';

    return buildEntry(authorStr, year, `${ref.title}.${doi}`);
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Format a single reference in APA7 bibliography style.
 */
export function formatApaBibliography(ref: ReferenceItem): string {
    const csl = ref.csl;

    if (!csl) {
        return formatFallback(ref);
    }

    switch (csl.type) {
        case 'article-journal':
        case 'article-magazine':
        case 'article-newspaper':
            return formatArticleJournal(csl);
        case 'book':
            return formatBook(csl);
        case 'chapter':
            return formatChapter(csl);
        case 'thesis':
            return formatThesis(csl);
        case 'webpage':
        case 'post-weblog':
        case 'online':
            return formatWebpage(csl);
        default:
            // Generic: try article-journal layout, fall back to book
            return csl['container-title']
                ? formatArticleJournal(csl)
                : formatBook(csl);
    }
}

/**
 * Sort references alphabetically by first author last name (APA7 rule).
 */
export function sortApa(refs: ReferenceItem[]): ReferenceItem[] {
    return [...refs].sort((a, b) => {
        const aA = Array.isArray(a.authors)
            ? (a.authors[0] ?? '')
            : (a.authors ?? '');
        const bA = Array.isArray(b.authors)
            ? (b.authors[0] ?? '')
            : (b.authors ?? '');
        const aLast = aA.trim().split(/\s+/).at(-1) ?? aA;
        const bLast = bA.trim().split(/\s+/).at(-1) ?? bA;

        return aLast.localeCompare(bLast, undefined, { sensitivity: 'base' });
    });
}

/**
 * Format the inline APA citation label for a reference.
 * Returns "(Author, year)" or "(Author et al., year)" for 3+ authors.
 */
export function formatApaInline(ref: ReferenceItem): string {
    const authors = Array.isArray(ref.authors)
        ? ref.authors
        : [ref.authors ?? ''];
    const firstAuthor = authors[0] ?? '';
    const parts = firstAuthor.trim().split(/\s+/);
    const lastName = parts.at(-1) ?? firstAuthor;
    const etAl = authors.length > 2 ? ' et al.' : '';
    const year = ref.year ?? '';

    return `(${lastName}${etAl}, ${year})`;
}
